// Passkeys in de browser (v5.8.0): inloggen zonder wachtwoord (loginpagina),
// een passkey toevoegen (instellingen en de verplichte inschrijfpagina) en de
// tweede stap met een passkey (verificatiepagina). De server doet alle
// controles; dit script vraagt de browser om de handtekening en stuurt die door.
//
//   data-controller="passkey"
//   data-passkey-options-url-value   POST: opties met een eenmalige challenge
//   data-passkey-submit-url-value    POST: het antwoord van de browser
//   data-passkey-redirect-value      (optioneel) waarheen na succes; anders
//                                    wat de server zegt, anders herladen
//   data-passkey-autofill-value      (inloggen) passkeys ook in het e-mailveld
//                                    aanbieden (conditional mediation)
//   data-passkey-i18n-value          teksten: cancelled, failed, unsupported
//   targets: message, password, remember, trust, unsupported, supported
import { Controller } from '@hotwired/stimulus'
import { toCreationOptions, toRequestOptions, credentialToJSON, passkeysSupported } from '../lib/webauthn_json'

const FALLBACK = {
  cancelled: 'Cancelled, or the passkey took too long. Try again.',
  failed: 'That did not work. Try again, or use another way to sign in.',
  unsupported: 'This browser does not support passkeys.',
}

function csrfToken () {
  const el = document.querySelector('meta[name="csrf-token"]')
  return el ? el.content : ''
}

async function postJSON (url, payload) {
  const r = await fetch(url, {
    method: 'POST',
    headers: { 'X-CSRF-Token': csrfToken(), 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload || {}),
    credentials: 'same-origin',
  })
  let data = {}
  try { data = await r.json() } catch (e) { data = {} }
  if (!r.ok) {
    const err = new Error(data.error || ('HTTP ' + r.status))
    err.serverMessage = data.error || null
    throw err
  }
  return data
}

export default class extends Controller {
  static values = { optionsUrl: String, submitUrl: String, redirect: String, autofill: Boolean, i18n: Object }
  static targets = ['message', 'password', 'remember', 'trust', 'unsupported', 'supported']

  connect () {
    const ok = passkeysSupported(window)
    this.unsupportedTargets.forEach((el) => { el.hidden = ok })
    this.supportedTargets.forEach((el) => { el.hidden = !ok })
    if (ok && this.autofillValue) this.startAutofill()
  }

  disconnect () {
    this.abortAutofill()
  }

  text (key) {
    return (this.hasI18nValue && this.i18nValue[key]) || FALLBACK[key]
  }

  show (message) {
    if (!this.hasMessageTarget) return
    this.messageTarget.textContent = message || ''
    this.messageTarget.hidden = !message
  }

  done (data) {
    const to = (data && data.redirect) || this.redirectValue
    // Alleen een pad binnen de app (v5.9.0): geen `//host` of volledige URL.
    if (to && /^\/(?![\/\\])/.test(to)) window.location.assign(to)
    else window.location.reload()
  }

  fail (error) {
    if (error && (error.name === 'NotAllowedError' || error.name === 'AbortError')) return this.show(this.text('cancelled'))
    this.show((error && error.serverMessage) || this.text('failed'))
  }

  // ---- Inloggen zonder wachtwoord ----
  async signIn (event) {
    if (event) event.preventDefault()
    if (!passkeysSupported(window)) return this.show(this.text('unsupported'))
    this.abortAutofill()
    this.show('')
    try {
      const options = await postJSON(this.optionsUrlValue)
      const credential = await navigator.credentials.get({ publicKey: toRequestOptions(options) })
      this.done(await postJSON(this.submitUrlValue, { credential: credentialToJSON(credential), remember_me: this.rememberChecked() }))
    } catch (error) { this.fail(error) }
  }

  // Passkeys ook aanbieden als suggestie in het e-mailveld (Safari, Chrome):
  // een openstaand verzoek dat pas afgaat als de gebruiker er een kiest.
  async startAutofill () {
    if (!window.PublicKeyCredential || typeof PublicKeyCredential.isConditionalMediationAvailable !== 'function') return
    try {
      if (!(await PublicKeyCredential.isConditionalMediationAvailable())) return
      this.autofillAbort = new AbortController()
      const options = await postJSON(this.optionsUrlValue)
      const credential = await navigator.credentials.get({
        publicKey: toRequestOptions(options), mediation: 'conditional', signal: this.autofillAbort.signal,
      })
      this.done(await postJSON(this.submitUrlValue, { credential: credentialToJSON(credential), remember_me: this.rememberChecked() }))
    } catch (error) {
      if (error && error.name === 'AbortError') return
      this.fail(error)
    }
  }

  abortAutofill () {
    if (this.autofillAbort) { this.autofillAbort.abort(); this.autofillAbort = null }
  }

  rememberChecked () {
    return this.hasRememberTarget ? this.rememberTarget.checked : false
  }

  // ---- Een passkey toevoegen ----
  async register (event) {
    if (event) event.preventDefault()
    if (!passkeysSupported(window)) return this.show(this.text('unsupported'))
    this.show('')
    try {
      const payload = this.hasPasswordTarget ? { current_password: this.passwordTarget.value } : {}
      const options = await postJSON(this.optionsUrlValue, payload)
      const credential = await navigator.credentials.create({ publicKey: toCreationOptions(options) })
      this.done(await postJSON(this.submitUrlValue, { credential: credentialToJSON(credential) }))
    } catch (error) { this.fail(error) }
  }

  // ---- De tweede stap met een passkey ----
  async verify (event) {
    if (event) event.preventDefault()
    if (!passkeysSupported(window)) return this.show(this.text('unsupported'))
    this.show('')
    try {
      const options = await postJSON(this.optionsUrlValue)
      const credential = await navigator.credentials.get({ publicKey: toRequestOptions(options) })
      const trust = this.hasTrustTarget ? this.trustTarget.checked : false
      this.done(await postJSON(this.submitUrlValue, { credential: credentialToJSON(credential), trust_device: trust }))
    } catch (error) { this.fail(error) }
  }
}
